export type NotificationStatus = 'Pending' | 'Sent' | 'Failed' | 'Bounced' | 'Held';
export type DeliveryMode = 'Send' | 'Hold' | 'Redirect';

export const NOTIFICATION_STATUSES: NotificationStatus[] =
  ['Pending', 'Sent', 'Failed', 'Bounced', 'Held'];

/** MatDialog widths for the notification modals. */
export const MODAL_WIDTHS = {
  /** Wide modals: message viewer and template diff. */
  wide: '820px',
  /** Narrow modals: the requeue confirmation. */
  narrow: '460px'
} as const;

/** Compact row for the message-log table. */
export interface NotificationMessageSummary {
  id: string;
  createdAt: string;
  status: NotificationStatus;
  toName?: string;
  toAddress: string;
  subject: string;
  caseId?: number;
  templateName: string;
  templateVersion: number;
  triggerEvent: string;
  recipientRole: string;
  deliveryMode: DeliveryMode;
  sentAt?: string;
  failedAt?: string;
  bouncedAt?: string;
  attemptCount: number;
}

/** Full message for the detail dialog (adds the rendered body + diagnostics). */
export interface NotificationMessageDetail extends NotificationMessageSummary {
  queuedAt: string;
  triggerSourceUserId?: number;
  taskId?: number;
  recipientUserId?: number;
  ccAddressesJSON?: string;
  bccAddressesJSON?: string;
  bodyHtmlRendered: string;
  templateDataJSON?: string;
  redirectedToAddress?: string;
  failureReason?: string;
  maxAttempts: number;
  nextAttemptAt?: string;
  bounceReason?: string;
}

export interface NotificationMessageFilter {
  status?: NotificationStatus;
  caseId?: number;
  recipient?: string;
  from?: string;
  to?: string;
  limit?: number;
  /** Keyset cursor for infinite scroll — the (createdAt, id) of the last loaded row. */
  beforeCreatedAt?: string;
  beforeId?: string;
}

/** Body of POST /notification/messages/{id}/requeue. targetMode must not be Hold. */
export interface RequeueRequest {
  targetMode: 'Send' | 'Redirect';
  redirectAddress?: string;
}

// ─── Templates ──────────────────────────────────────────────

/** Row in GET /notification/templates. */
export interface TemplateSummary {
  name: string;
  description: string;
  currentVersion: number;
}

/** Current (joined latest) template from GET /notification/templates/{name}. */
export interface CurrentTemplate {
  name: string;
  currentVersion: number;
  subject: string;
  bodyHtml: string;
  defaultCcAddresses?: string;
  defaultBccAddresses?: string;
  description: string;
  /** Base64 optimistic-concurrency token; echoed back unchanged on save. */
  rowVersion: string;
}

/** A historical template version (GET .../history items and .../versions/{n}). */
export interface TemplateVersion {
  versionNumber: number;
  subject: string;
  bodyHtml: string;
  defaultCcAddresses?: string;
  defaultBccAddresses?: string;
  changeNote?: string;
  updatedAt: string;
  updatedByUserId?: number;
}

/**
 * Names of the server-defined template variables (the `name` field of a
 * `SchemaVar`). Referenced by components that look up specific variables by
 * name (e.g. the preview's "To" line). Keep in sync with the server schema.
 */
export const TEMPLATE_VARIABLE_NAMES = {
  CandidateName: 'CandidateName',
  RecipientName: 'RecipientName',
  TaskLink: 'TaskLink'
} as const;

/** A variable available to a template, from GET .../{name}/schema. */
export interface SchemaVar {
  name: string;
  description: string;
  /** True for raw-HTML variables (ready-made links): emit with triple braces {{{name}}}. */
  html: boolean;
  sample: string;
}

/** Body of PUT /notification/templates/{name}. */
export interface SaveTemplateRequest {
  subject: string;
  bodyHtml: string;
  defaultCcAddresses?: string;
  defaultBccAddresses?: string;
  changeNote?: string;
  /** The rowVersion the editor loaded (base64) — optimistic-concurrency token. */
  expectedRowVersion: string;
}

/**
 * Result of a save attempt. `conflict` is true when the server rejected the
 * save (HTTP 409) because another admin saved first; `template` then carries
 * the latest server state for the "reload" prompt.
 */
export interface SaveTemplateResult {
  conflict: boolean;
  template: CurrentTemplate;
}
